import React, { useState, useEffect, useCallback, useMemo } from 'react';
import moment from 'moment';
import Cookies from 'js-cookie';
import 'moment/locale/ja';

moment.locale('ja');

const SHIFTS_API = 'https://system.sales-management.cloud/api/shifts.php';
const USERS_API = 'https://system.sales-management.cloud/api/users.php';
const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

const isSameUserId = (a, b) => String(a) === String(b);
const getInternalUserId = (user) => String(user?.id ?? user?.user_id ?? '');

const hasShiftTime = (shift) => {
  const candidates = [
    shift?.start_time,
    shift?.end_time,
    shift?.shift_in,
    shift?.shift_out,
  ];
  return candidates.some((value) => value != null && String(value).trim() !== '');
};

function AdminShiftManagement() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarInfo, setCalendarInfo] = useState(null);
  const [users, setUsers] = useState([]);
  const [shifts, setShifts] = useState({});
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showShiftEditor, setShowShiftEditor] = useState(false);
  const [editingShift, setEditingShift] = useState(null);

  const today = moment().format('YYYY-MM-DD');

  // 認証データ取得
  const getAuthData = useCallback(() => {
    const token = Cookies.get('auth_token');
    const userInfo = Cookies.get('user_info');
    const user = userInfo ? JSON.parse(userInfo) : null;
    return { token, user };
  }, []);

  // ユーザー一覧取得
  const fetchUsers = useCallback(async () => {
    try {
      const { token } = getAuthData();
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const response = await fetch(`${USERS_API}?limit=500&sort_by=name`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.status === 'success') {
        const usersList = data.data?.users || [];
        setUsers(usersList);
        if (usersList.length > 0) {
          setSelectedUserId(getInternalUserId(usersList[0]));
        }
      } else {
        setError(data.message || 'ユーザー一覧の取得に失敗しました');
      }
    } catch (err) {
      console.error('Fetch users error:', err);
      setError('通信エラーが発生しました');
    }
  }, [getAuthData]);

  // シフトデータ取得
  const fetchShifts = useCallback(async (date = currentDate) => {
    setLoading(true);
    setError('');

    try {
      const { token } = getAuthData();
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const year = moment(date).format('YYYY');
      const month = moment(date).format('M');
      const selectedUserForRequest = users.find((u) => isSameUserId(getInternalUserId(u), selectedUserId));
      const requestUserId = selectedUserForRequest?.user_id || selectedUserId;

      // 管理者向けは user_id パラメータを指定
      const params = new URLSearchParams({
        year,
        month,
        ...(requestUserId && { user_id: String(requestUserId) })
      });

      const response = await fetch(`${SHIFTS_API}?${params.toString()}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.status === 'success') {
        setCalendarInfo(data.calendar_info || null);
        
        // データ処理
        const shiftsMap = {};
        if (data.data) {
          Object.keys(data.data).forEach(dateKey => {
            const dayData = data.data[dateKey];
            shiftsMap[dateKey] = dayData.shifts || [];
          });
        }
        setShifts(shiftsMap);
      } else {
        setError(data.message || 'シフトの取得に失敗しました');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('通信エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }, [currentDate, selectedUserId, users, getAuthData]);

  // 初期化
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts, selectedUserId]);

  // シフト追加・編集
  const handleSaveShift = useCallback(async (dateStr, userId, shiftData) => {
    setError('');
    setSuccess('');

    try {
      const { token } = getAuthData();
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      // 有効状態チェック
      if (!shiftData.enabled) {
        // 削除処理
        const response = await fetch(SHIFTS_API, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            date: dateStr,
            user_id: userId
          })
        });

        const data = await response.json();
        if (data.status === 'success' || data.message === 'Shift not found for this date') {
          setSuccess('シフトを削除しました');
          fetchShifts();
          setShowShiftEditor(false);
          setEditingShift(null);
        } else {
          setError(data.message || 'シフトの削除に失敗しました');
        }
        return;
      }

      // POST リクエストでシフト更新
      const response = await fetch(SHIFTS_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          date: dateStr,
          user_id: userId,
          shift_in: shiftData.shift_in || '09:00',
          shift_out: shiftData.shift_out || '18:00'
        })
      });

      const data = await response.json();

      if (data.status === 'success') {
        setSuccess('シフトを保存しました');
        fetchShifts();
        setShowShiftEditor(false);
        setEditingShift(null);
      } else {
        setError(data.message || 'シフトの保存に失敗しました');
      }
    } catch (err) {
      console.error('Save shift error:', err);
      setError('通信エラーが発生しました');
    }
  }, [getAuthData, fetchShifts]);

  // シフト削除
  const handleDeleteShift = useCallback(async (dateStr, userId) => {
    if (!window.confirm('このシフトを削除しますか?')) return;

    setError('');
    setSuccess('');

    try {
      const { token } = getAuthData();
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const response = await fetch(SHIFTS_API, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          date: dateStr,
          user_id: userId
        })
      });

      const data = await response.json();

      if (data.status === 'success' || data.message === 'Shift not found for this date') {
        setSuccess('シフトを削除しました');
        fetchShifts();
      } else {
        setError(data.message || 'シフトの削除に失敗しました');
      }
    } catch (err) {
      console.error('Delete shift error:', err);
      setError('通信エラーが発生しました');
    }
  }, [getAuthData, fetchShifts]);

  // カレンダー日付生成
  const generateCalendarDays = useCallback(() => {
    const start = calendarInfo
      ? moment(calendarInfo.start_date)
      : moment(currentDate).startOf('month');
    const end = calendarInfo
      ? moment(calendarInfo.end_date)
      : moment(currentDate).endOf('month');

    const days = [];

    for (let i = 0; i < start.day(); i++) {
      days.push(null);
    }

    let current = start.clone();
    while (current.isSameOrBefore(end)) {
      days.push(current.clone());
      current.add(1, 'day');
    }

    return days;
  }, [calendarInfo, currentDate]);

  // 月移動
  const navigateMonth = useCallback((offset) => {
    const newDate = moment(currentDate).add(offset, 'month').toDate();
    setCurrentDate(newDate);
  }, [currentDate]);

  const handlePrevMonth = () => navigateMonth(-1);
  const handleNextMonth = () => navigateMonth(1);
  const handleToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const calendarDays = generateCalendarDays();

  // 選択ユーザーのシフト情報
  const selectedUser = useMemo(
    () => users.find((u) => isSameUserId(getInternalUserId(u), selectedUserId)),
    [users, selectedUserId]
  );

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <Header
        currentDate={currentDate}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
        onToday={handleToday}
      />

      <MessageDisplay error={error} success={success} />

      {/* ユーザー選択 */}
      <UserSelector
        users={users}
        selectedUserId={selectedUserId}
        onUserSelect={setSelectedUserId}
      />

      {loading ? (
        <LoadingSpinner />
      ) : selectedUser ? (
        <>
          <CalendarGrid
            calendarDays={calendarDays}
            user={selectedUser}
            today={today}
            shifts={shifts}
            currentDate={currentDate}
            onEditShift={(date) => {
              const dateStr = moment(date).format('YYYY-MM-DD');
              const dayShifts = shifts[dateStr] || [];
              const userShift = dayShifts.find((s) => isSameUserId(s.user_id, getInternalUserId(selectedUser)));
              setEditingShift({ date, shift: userShift });
              setShowShiftEditor(true);
            }}
            onDeleteShift={(date) => {
              const dateStr = moment(date).format('YYYY-MM-DD');
              handleDeleteShift(dateStr, selectedUser?.user_id || selectedUserId);
            }}
            onAddShift={(date) => {
              setEditingShift({ date, shift: null });
              setShowShiftEditor(true);
            }}
          />

          {/* シフト編集モーダル */}
          {showShiftEditor && editingShift && (
            <ShiftEditorModal
              date={editingShift.date}
              shift={editingShift.shift}
              user={selectedUser}
              onSave={(shiftData) => {
                const dateStr = moment(editingShift.date).format('YYYY-MM-DD');
                handleSaveShift(dateStr, selectedUser?.user_id || selectedUserId, shiftData);
              }}
              onClose={() => {
                setShowShiftEditor(false);
                setEditingShift(null);
              }}
            />
          )}
        </>
      ) : (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-700">ユーザーを選択してください</p>
        </div>
      )}
    </div>
  );
}

// ヘッダー
const Header = ({ currentDate, onPrevMonth, onNextMonth, onToday }) => (
  <div className="mb-6">
    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">シフト管理（管理者用）</h1>
    
    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 mb-4">
      <div className="flex items-center gap-2">
        <button
          onClick={onPrevMonth}
          className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm"
        >
          前月
        </button>
        <button
          onClick={onToday}
          className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
        >
          今月
        </button>
        <button
          onClick={onNextMonth}
          className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm"
        >
          次月
        </button>
      </div>
      
      <h2 className="text-lg md:text-xl font-semibold text-gray-800">
        {moment(currentDate).format('YYYY年M月')}
      </h2>
    </div>
  </div>
);

// メッセージ表示
const MessageDisplay = ({ error, success }) => (
  <>
    {error && (
      <div className="mb-4 p-3 md:p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-xs md:text-sm text-red-600">{error}</p>
      </div>
    )}
    {success && (
      <div className="mb-4 p-3 md:p-4 bg-green-50 border border-green-200 rounded-md">
        <p className="text-xs md:text-sm text-green-600">{success}</p>
      </div>
    )}
  </>
);

// ローディング
const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-20 text-gray-500">
    読み込み中...
  </div>
);

// ユーザー選択
const UserSelector = ({ users, selectedUserId, onUserSelect }) => (
  <div className="mb-6 p-4 bg-white border border-gray-200 rounded-md shadow-sm">
    <label className="block text-sm font-medium text-gray-700 mb-2">
      ユーザーを選択
    </label>
    <select
      value={selectedUserId || ''}
      onChange={(e) => onUserSelect(e.target.value)}
      className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      <option value="">-- ユーザーを選択 --</option>
      {users.map((user) => (
        <option key={getInternalUserId(user)} value={getInternalUserId(user)}>
          {user.emoji || '👤'} {user.name} ({user.user_id})
        </option>
      ))}
    </select>
  </div>
);

// カレンダーグリッド
const CalendarGrid = ({
  calendarDays,
  user,
  today,
  shifts,
  currentDate,
  viewMode,
  onEditShift,
  onDeleteShift,
  onAddShift
}) => {
  return (
    <div className="rounded-lg shadow-sm md:shadow-md overflow-hidden bg-white">
      <WeekdayHeader />
      <div className="grid grid-cols-7 gap-px bg-gray-100">
        {calendarDays.map((day, index) => (
          <CalendarDay
            key={day ? day.format('YYYY-MM-DD') : `empty-${index}`}
            day={day}
            user={user}
            today={today}
            shifts={shifts}
            onEditShift={onEditShift}
            onDeleteShift={onDeleteShift}
            onAddShift={onAddShift}
          />
        ))}
      </div>
    </div>
  );
};

// 曜日ヘッダー
const WeekdayHeader = () => (
  <div className="grid grid-cols-7 bg-gray-50">
    {WEEKDAYS.map((day, index) => (
      <div
        key={index}
        className={`p-3 text-center text-sm font-semibold ${
          index === 0 ? 'text-red-600' : index === 6 ? 'text-blue-600' : 'text-gray-700'
        }`}
      >
        {day}
      </div>
    ))}
  </div>
);

// カレンダー日付セル
const CalendarDay = ({
  day,
  user,
  today,
  shifts,
  onEditShift,
  onDeleteShift,
  onAddShift
}) => {
  if (!day) {
    return <div className="p-2 md:min-h-32" />;
  }

  const dateStr = day.format('YYYY-MM-DD');
  const isToday = dateStr === today;
  const isPast = moment(dateStr).isBefore(today, 'day');

  const dayShifts = shifts[dateStr] || [];
  const userShift = dayShifts.find((s) => isSameUserId(s.user_id, getInternalUserId(user)));
  const hasShift = userShift && hasShiftTime(userShift);

  return (
    <div
      className={`p-2 md:p-3 md:min-h-32 ${
        isToday ? 'bg-blue-50' : isPast ? 'bg-amber-50' : 'bg-white'
      } transition-colors md:hover:bg-gray-50 border border-gray-200`}
    >
      <DayNumber day={day} isToday={isToday} isPast={isPast} />
      
      <div className="space-y-2">
        {hasShift ? (
          <>
            <ShiftDisplay
              shift={userShift}
            />
            <div className="flex gap-1">
              <button
                onClick={() => onEditShift(day)}
                className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
                  isPast ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                }`}
                title={isPast ? '過去の日程も編集可能' : '編集'}
              >
                編集
              </button>
              <button
                onClick={() => onDeleteShift(day)}
                className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
                  isPast ? 'bg-red-200 text-red-800 hover:bg-red-300' : 'bg-red-50 text-red-700 hover:bg-red-100'
                }`}
                title={isPast ? '過去の日程も削除可能' : '削除'}
              >
                削除
              </button>
            </div>
          </>
        ) : (
          <button
            onClick={() => onAddShift(day)}
            className={`w-full px-2 py-1 text-xs rounded transition-colors ${
              isPast ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-green-50 text-green-700 hover:bg-green-100'
            }`}
            title={isPast ? '過去の日程にも追加可能' : 'シフト追加'}
          >
            {isPast ? '⚠ 過去日' : '+'} シフト{isPast ? '追加' : '追加'}
          </button>
        )}
      </div>
    </div>
  );
};

// 日付番号
const DayNumber = ({ day, isToday, isPast }) => (
  <div className="flex items-baseline gap-1 mb-2">
    <span className={`text-base md:text-sm font-semibold ${
      isToday ? 'text-blue-700' :
      isPast ? 'text-amber-700' :
      day.day() === 0 ? 'text-red-600' :
      day.day() === 6 ? 'text-blue-600' :
      'text-gray-800'
    }`}>
      {day.format('D')}
    </span>
    <span className={`text-[10px] md:text-xs ${
      isPast ? 'text-amber-600' : 'text-gray-400'
    }`}>{WEEKDAYS[day.day()]}</span>
  </div>
);

// シフト表示
const ShiftDisplay = ({ shift }) => {
  const checkIn = shift?.shift_in ? String(shift.shift_in).substring(0, 5) : '';
  const checkOut = shift?.shift_out ? String(shift.shift_out).substring(0, 5) : '';

  if (!checkIn || !checkOut) {
    return <div className="text-xs bg-gray-100 text-gray-600 p-1 rounded">シフト情報なし</div>;
  }

  return (
    <div className="text-xs bg-green-100 text-green-800 p-1 rounded">
      {checkIn} - {checkOut}
    </div>
  );
};

// シフト編集モーダル
const ShiftEditorModal = ({ date, shift, user, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    enabled: shift ? true : false,
    shift_in: shift?.shift_in ? String(shift.shift_in).substring(0, 5) : '09:00',
    shift_out: shift?.shift_out ? String(shift.shift_out).substring(0, 5) : '18:00'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          {user.name} - {moment(date).format('YYYY年M月D日(ddd)')}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="enabled"
              checked={formData.enabled}
              onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            />
            <label htmlFor="enabled" className="text-sm font-medium text-gray-700">
              {formData.enabled ? 'シフト有り' : 'シフト無し'}
            </label>
          </div>

          {formData.enabled && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  チェックイン時刻
                </label>
                <input
                  type="time"
                  value={formData.shift_in}
                  onChange={(e) => setFormData({ ...formData, shift_in: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  チェックアウト時刻
                </label>
                <input
                  type="time"
                  value={formData.shift_out}
                  onChange={(e) => setFormData({ ...formData, shift_out: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
            >
              保存
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-medium"
            >
              キャンセル
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminShiftManagement;
